/**
 * Product and project registry
 *
 * Language-independent facts and presentation metadata for every TamerinTECH
 * product. Translated copy (names, status labels, descriptions) lives in
 * src/i18n/translations.ts under `products.items`, keyed by ProductId, so that
 * the same product is described consistently on the page, in the footer, for
 * AI agents (WebMCP) and in tests.
 *
 * ContextFriend is the main product. MBot is the live product. LONDI is an
 * ongoing research collaboration. TIMA and SidAI are concluded deployments
 * that remain as references.
 */
import type { Language } from './i18n/LanguageContext';

export type ProductId = 'contextfriend' | 'mbot' | 'londi' | 'tima' | 'sidai';

/**
 * Canonical status vocabulary. Every project carries exactly one of these.
 * The translated label rendered next to it lives in translations.
 */
export type ProductStatus = 'prototype' | 'live' | 'research' | 'concluded';

export interface ProductDefinition {
  id: ProductId;
  /** Short, language-independent name for footers, agent tools and tests. */
  shortName: string;
  status: ProductStatus;
  /** The one product that owns the page hierarchy. */
  featured?: boolean;
  image?: string;
  /** Language-specific variant of `image` (e.g. chalkboard text in the photo). */
  imageByLanguage?: Record<Language, string>;
  imageCredit?: string;
  /** Clean canonical destination per language, without tracking parameters. Absent for concluded projects without a live site. */
  destinationByLanguage?: Record<Language, string>;
  /** Secondary destination: pilot application page. */
  pilotByLanguage?: Record<Language, string>;
  playStoreUrl?: string;
  /** When set, outbound links carry first-party referral attribution. */
  referral?: { campaign: string };
  /** Machine-readable note for agents, e.g. when a concluded project ended. */
  concluded?: string;
}

export const CONTEXTFRIEND_SITE = 'https://context-friend.com';

export const PRODUCTS: readonly ProductDefinition[] = [
  {
    id: 'contextfriend',
    shortName: 'ContextFriend',
    status: 'prototype',
    featured: true,
    image: '/contextfriend-device-en.jpg',
    imageByLanguage: {
      en: '/contextfriend-device-en.jpg',
      de: '/contextfriend-device-de.jpg'
    },
    destinationByLanguage: {
      en: `${CONTEXTFRIEND_SITE}/en/`,
      de: `${CONTEXTFRIEND_SITE}/de/`
    },
    pilotByLanguage: {
      en: `${CONTEXTFRIEND_SITE}/en/pilot`,
      de: `${CONTEXTFRIEND_SITE}/de/pilot`
    },
    referral: { campaign: 'contextfriend_2026' }
  },
  {
    id: 'mbot',
    shortName: 'MBot',
    status: 'live',
    image: '/mbot-kids-1.jpg',
    destinationByLanguage: {
      en: 'https://mbot.tamerintech.com',
      de: 'https://mbot.tamerintech.com'
    },
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.tamerintech.mbot'
  },
  {
    id: 'londi',
    shortName: 'LONDI',
    status: 'research',
    image: '/londi-screenshot.png',
    destinationByLanguage: {
      en: 'https://www.londi.de/',
      de: 'https://www.londi.de/'
    }
  },
  {
    id: 'tima',
    shortName: 'TIMA',
    status: 'concluded',
    concluded: '2024',
    image: '/tima-screenshot.png',
    imageCredit: 'Photo: Deutsches Museum Bonn'
  },
  {
    id: 'sidai',
    shortName: 'SidAI',
    status: 'concluded',
    concluded: '2025-04',
    image: '/sidai-screenshot.png'
  }
];

/** Where on the site a product link is placed; becomes `utm_content`. */
export type ProductPlacement =
  | 'homepage_hero'
  | 'homepage_featured'
  | 'homepage_contact'
  | 'footer_products'
  | 'chat_cta';

export const getProduct = (id: ProductId): ProductDefinition => {
  const product = PRODUCTS.find((entry) => entry.id === id);
  if (!product) {
    throw new Error(`Unknown product: ${id}`);
  }
  return product;
};

const withReferral = (destination: string, product: ProductDefinition, placement: ProductPlacement): string => {
  if (!product.referral) {
    return destination;
  }
  const url = new URL(destination);
  url.searchParams.set('utm_source', 'tamerin.tech');
  url.searchParams.set('utm_medium', 'referral');
  url.searchParams.set('utm_campaign', product.referral.campaign);
  url.searchParams.set('utm_content', placement);
  return url.toString();
};

/**
 * Outbound link for a product in the given language, or null when the project
 * has no live site. Products with referral tracking get a consistent UTM set;
 * only `utm_content` varies by placement. Canonical (structured-data) URLs
 * must use `destinationByLanguage` directly.
 */
export const productUrl = (
  product: ProductDefinition,
  language: Language,
  placement: ProductPlacement
): string | null => {
  const destination = product.destinationByLanguage?.[language];
  if (!destination) {
    return null;
  }
  return withReferral(destination, product, placement);
};

/** Link to the pilot application page of a product, with the same attribution. */
export const productPilotUrl = (
  product: ProductDefinition,
  language: Language,
  placement: ProductPlacement
): string | null => {
  const destination = product.pilotByLanguage?.[language];
  if (!destination) {
    return null;
  }
  return withReferral(destination, product, placement);
};
